import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useContent, resolveMediaUrl } from "@/context/ContentContext";

const DEFAULT_DINING = "https://images.unsplash.com/photo-1542557497-4c7b03d0d245?auto=format&fit=crop&w=1400&q=80";

export default function BookingPage() {
  const { content } = useContent();
  const diningImg = resolveMediaUrl(content?.images?.dining) || DEFAULT_DINING;
  const navigate = useNavigate();
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [party, setParty] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [requests, setRequests] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const dateStr = useMemo(() => (date ? format(date, "yyyy-MM-dd") : ""), [date]);

  useEffect(() => {
    if (!dateStr) return;
    setLoadingSlots(true);
    setTime("");
    api
      .get("/availability", { params: { date: dateStr } })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [dateStr]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!dateStr || !time || !party || !name || !email || !phone) {
      setError("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reservations", {
        name,
        email,
        phone,
        party_size: Number(party),
        date: dateStr,
        time_slot: time,
        special_requests: requests,
      });
      toast.success("Reservation received", { description: `Reference ${data.reference_code}` });
      navigate(`/confirmation/${data.reference_code}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="booking-page">
      <SiteHeader />
      <div className="pt-24 grid md:grid-cols-2 min-h-screen">
        {/* Left visual */}
        <div className="relative hidden md:block">
          <img src={diningImg} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/60" />
          <div className="absolute bottom-0 p-10 text-white">
            <div className="overline text-white/70">Book your table</div>
            <h2 className="font-display text-4xl mt-3 leading-tight max-w-xs">
              Front‑row to the caldera at sunset.
            </h2>
          </div>
        </div>

        {/* Form */}
        <div className="px-6 md:px-14 py-12 md:py-20">
          <div className="max-w-md">
            <div className="overline">Reservation</div>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl">A table for you</h1>
            <p className="mt-3 text-sm text-[#8C7A6B]">
              Service · 10:00 – 22:00 · confirmed by the restaurant within a few hours.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-6" data-testid="booking-form">
              {/* Date */}
              <div>
                <Label className="overline">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      type="button"
                      className="mt-2 w-full justify-start bg-white border-[#E5E0D8] h-12 text-left font-normal text-[#1A2B3C] hover:bg-white"
                      data-testid="booking-date-trigger"
                    >
                      <CalendarIcon className="mr-3 h-4 w-4 text-[#D35400]" />
                      {date ? format(date, "EEEE, d LLLL yyyy") : "Choose a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time + Party */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Time</Label>
                  <Select value={time} onValueChange={setTime} disabled={!dateStr || loadingSlots}>
                    <SelectTrigger className="mt-2 h-12 bg-white border-[#E5E0D8]" data-testid="booking-time-trigger">
                      <SelectValue placeholder={loadingSlots ? "Loading…" : "Pick a time"} />
                    </SelectTrigger>
                    <SelectContent>
                      {slots.map((s) => (
                        <SelectItem
                          key={s.time}
                          value={s.time}
                          disabled={s.is_past || !s.is_available}
                          data-testid={`slot-${s.time}`}
                        >
                          {s.time}{!s.is_available && !s.is_past ? " · Full" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="overline">Guests</Label>
                  <Select value={party} onValueChange={setParty}>
                    <SelectTrigger className="mt-2 h-12 bg-white border-[#E5E0D8]" data-testid="booking-party-trigger">
                      <SelectValue placeholder="Party size" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }).map((_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)} data-testid={`party-${i + 1}`}>
                          {i + 1} {i === 0 ? "guest" : "guests"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="name" className="overline">Full name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 h-12 bg-white border-[#E5E0D8]"
                  data-testid="booking-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="overline">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 h-12 bg-white border-[#E5E0D8]"
                    data-testid="booking-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="overline">Phone</Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="mt-2 h-12 bg-white border-[#E5E0D8]"
                    data-testid="booking-phone"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="requests" className="overline">Special requests</Label>
                <Textarea
                  id="requests"
                  value={requests}
                  onChange={(e) => setRequests(e.target.value)}
                  rows={3}
                  placeholder="Allergies, celebrations, seating preference…"
                  className="mt-2 bg-white border-[#E5E0D8]"
                  data-testid="booking-requests"
                />
              </div>

              {error && (
                <div className="text-sm text-[#C0392B]" data-testid="booking-error">{error}</div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="btn-terracotta w-full h-12 rounded-full text-base"
                data-testid="booking-submit"
              >
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending request…</> : "Request Reservation"}
              </Button>
              <p className="text-xs text-[#8C7A6B] text-center">
                A member of the team will confirm your table shortly.
              </p>
            </form>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
